import { useState } from "react";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import SEO from "../../components/SEO";

export default function JsonFormatter() {

  const [input, setInput] =
    useState("");

  const [output, setOutput] =
    useState("");

  const [error, setError] =
    useState("");

  // FORMAT JSON

  const formatJSON = () => {

    try {

      setError("");

      const parsed =
        JSON.parse(input);

      setOutput(
        JSON.stringify(
          parsed,
          null,
          2
        )
      );

    } catch {

      setOutput("");

      setError(
        "Invalid JSON format"
      );

    }

  };

  // MINIFY JSON

  const minifyJSON = () => {

    try {

      setError("");

      const parsed =
        JSON.parse(input);

      setOutput(
        JSON.stringify(parsed)
      );

    } catch {

      setOutput("");

      setError(
        "Invalid JSON format"
      );

    }

  };

  // VALIDATE JSON

  const validateJSON = () => {

    try {

      JSON.parse(input);

      setError("");

      setOutput(
        "✅ Valid JSON"
      );

    } catch {

      setOutput("");

      setError(
        "❌ Invalid JSON"
      );

    }

  };

  // COPY OUTPUT

  const copyOutput = () => {

    navigator.clipboard.writeText(
      output
    );

    alert("Copied to clipboard");

  };

  // DOWNLOAD JSON

  const downloadJSON = () => {

    const blob =
      new Blob(
        [output],
        {
          type:
            "application/json",
        }
      );

    const url =
      URL.createObjectURL(blob);

    const a =
      document.createElement("a");

    a.href = url;

    a.download =
      "formatted.json";

    a.click();

    URL.revokeObjectURL(url);

  };

  // CLEAR

  const clearAll = () => {

    setInput("");
    setOutput("");
    setError("");

  };

  return (
    <>
      <SEO
        title="JSON Formatter Online"
        description="Beautify, minify, validate, and format JSON instantly online for free."
      />

      <Navbar />

      {/* HERO */}

      <section
        style={{
          background:
            "linear-gradient(135deg,#0f172a,#1e293b)",
          color: "white",
          padding:
            "70px 20px",
        }}
      >

        <div className="container text-center">

          <span className="badge bg-primary p-2 px-3 mb-4">
            Developer Tool
          </span>

          <h1
            className="fw-bold mb-4"
            style={{
              fontSize:
                "clamp(2.5rem,5vw,4rem)",
            }}
          >
            JSON Formatter
          </h1>

          <p
            className="lead mx-auto"
            style={{
              maxWidth: "850px",
            }}
          >
            Beautify,
            validate,
            and minify JSON instantly
            using this free online developer tool.
          </p>

        </div>

      </section>

      {/* TOOL */}

      <div className="container py-5">

        <div
          className="card border-0 shadow-lg rounded-4 p-4 p-md-5"
        >

          <h2 className="fw-bold mb-4">
            JSON Input
          </h2>

          <textarea
            rows="14"
            className="form-control"
            placeholder='Paste JSON here...'
            value={input}
            onChange={(e) =>
              setInput(e.target.value)
            }
            style={{
              resize: "none",
              fontFamily:
                "monospace",
            }}
          />

          {/* BUTTONS */}

          <div
            className="d-flex flex-wrap gap-3 mt-4"
          >

            <button
              className="btn btn-primary"
              onClick={formatJSON}
            >
              Beautify JSON
            </button>

            <button
              className="btn btn-success"
              onClick={minifyJSON}
            >
              Minify JSON
            </button>

            <button
              className="btn btn-warning"
              onClick={validateJSON}
            >
              Validate JSON
            </button>

            <button
              className="btn btn-outline-danger"
              onClick={clearAll}
            >
              Clear
            </button>

          </div>

          {/* ERROR */}

          {error && (

            <div className="alert alert-danger mt-4">

              {error}

            </div>

          )}

          {/* OUTPUT */}

          {output && (

            <div className="mt-5">

              <div
                className="d-flex justify-content-between align-items-center mb-3"
              >

                <h3 className="fw-bold mb-0">
                  Output
                </h3>

                <div
                  className="d-flex gap-2"
                >

                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={copyOutput}
                  >
                    Copy
                  </button>

                  {output !==
                    "✅ Valid JSON" && (

                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={downloadJSON}
                    >
                      Download
                    </button>

                  )}

                </div>

              </div>

              <pre
                className="bg-light p-4 rounded-4"
                style={{
                  overflowX:
                    "auto",
                  whiteSpace:
                    "pre-wrap",
                  wordBreak:
                    "break-word",
                  fontSize: "15px",
                }}
              >
                {output}
              </pre>

            </div>

          )}

        </div>

      </div>

      {/* SEO SECTION */}

      <div className="container pb-5">
  <div className="card border-0 shadow-sm rounded-4 p-4 p-md-5">

    <h2 className="fw-bold mb-4">What is a JSON Formatter?</h2>
    <p>
      A JSON Formatter is an online developer tool that takes raw, unformatted,
      or minified JSON data and reformats it into a clean, human-readable
      structure with proper indentation and line breaks. JSON (JavaScript Object
      Notation) is the most widely used data format for APIs, configuration
      files, databases, and web applications. When you receive JSON data from an
      API response or a log file, it often arrives as a single compressed line
      with no whitespace, making it nearly impossible to read or debug. Our JSON
      formatter instantly beautifies that data into a structured, indented format
      so you can clearly see every key, value, and nested object.
    </p>

    <h2 className="fw-bold mt-5 mb-4">How to Use This JSON Formatter</h2>
    <ol>
      <li className="mb-2">
        <strong>Paste your JSON</strong> into the input text area. You can paste
        minified JSON, API responses, configuration data, or any valid JSON string.
      </li>
      <li className="mb-2">
        <strong>Click Beautify JSON</strong> to format the JSON with proper
        indentation (2 spaces) and line breaks, making it easy to read and
        navigate.
      </li>
      <li className="mb-2">
        <strong>Click Minify JSON</strong> to compress the JSON into a single
        line with no whitespace — useful for reducing payload size in API
        requests or production code.
      </li>
      <li className="mb-2">
        <strong>Click Validate JSON</strong> to instantly check whether your
        JSON is syntactically correct. The tool will tell you if the JSON is
        valid or show an error message if there is a problem.
      </li>
      <li className="mb-2">
        <strong>Copy or Download</strong> the formatted output using the buttons
        above the result. You can copy it to your clipboard or save it as a
        .json file.
      </li>
    </ol>

    <h2 className="fw-bold mt-5 mb-4">Beautify vs Minify — What's the Difference?</h2>
    <p>
      Beautifying JSON (also called pretty-printing) adds indentation, line
      breaks, and spacing to make the structure visually clear. This is what
      you want when reading, debugging, or reviewing JSON data. Minifying JSON
      does the opposite — it removes all unnecessary whitespace to produce the
      most compact version of the data. Minified JSON is smaller in file size,
      which means faster transmission over networks and lower bandwidth usage.
      You would typically use beautified JSON during development and debugging,
      and minified JSON in production environments where performance matters.
    </p>

    <h2 className="fw-bold mt-5 mb-4">Common JSON Errors and How to Fix Them</h2>
    <p>
      The most common JSON errors that our validator catches include missing or
      extra commas (trailing commas after the last item in an array or object
      are not valid in JSON), unquoted keys (all JSON keys must be wrapped in
      double quotes, not single quotes), unclosed brackets or braces (every
      opening bracket must have a matching closing bracket), and incorrect data
      types (for example, using JavaScript-specific values like undefined or
      functions, which are not valid in JSON). If our validator shows an error,
      check these common issues first. The error message will also indicate
      where in the JSON the problem was detected.
    </p>

    <h2 className="fw-bold mt-5 mb-4">Who Uses a JSON Formatter?</h2>
    <p>
      JSON formatters are used daily by software developers, backend engineers,
      frontend developers, QA testers, and DevOps engineers. When working with
      REST APIs, developers frequently need to inspect response payloads to
      verify data structure and values. Database administrators use JSON
      formatters when working with document databases like MongoDB or Firebase
      that store data in JSON format. Configuration engineers format JSON config
      files for applications and cloud services. Even non-developers like product
      managers and data analysts use JSON formatters to make sense of API
      outputs they receive from technical teams.
    </p>

    <h2 className="fw-bold mt-5 mb-4">Frequently Asked Questions</h2>

    <div className="mb-4">
      <h5>Is this JSON formatter free?</h5>
      <p>
        Yes, this JSON formatter is completely free to use with no account or
        registration required. You can format, minify, validate, copy, and
        download JSON as many times as you need at no cost.
      </p>
    </div>

    <div className="mb-4">
      <h5>Is my JSON data secure?</h5>
      <p>
        Yes. All JSON processing happens entirely inside your browser using
        JavaScript. Your data is never sent to any server or stored anywhere.
        This makes our tool safe to use even with sensitive configuration data
        or internal API responses.
      </p>
    </div>

    <div className="mb-4">
      <h5>What is the difference between JSON and JavaScript objects?</h5>
      <p>
        JSON is a text-based data format derived from JavaScript object syntax,
        but it is stricter. In JSON, all keys must be double-quoted strings,
        values can only be strings, numbers, booleans, null, arrays, or objects,
        and trailing commas are not allowed. JavaScript objects are more
        flexible and support functions, undefined values, single-quoted strings,
        and unquoted keys. JSON is language-independent and used for data
        exchange between systems, while JavaScript objects exist only within
        JavaScript code.
      </p>
    </div>

    <div className="mb-4">
      <h5>Can I format nested JSON?</h5>
      <p>
        Yes. Our formatter handles JSON of any complexity including deeply nested
        objects and arrays. The indentation clearly shows each level of nesting,
        making it easy to navigate complex data structures with many levels of
        hierarchy.
      </p>
    </div>

    <div className="mt-5 p-4 bg-light rounded-4">
      <h3 className="fw-bold mb-3">Related Developer Tools</h3>
      <div className="d-flex flex-wrap gap-3">
        <a href="/jwt-decoder" className="btn btn-outline-primary">JWT Decoder</a>
        <a href="/password-generator" className="btn btn-outline-primary">Password Generator</a>
        <a href="/word-counter" className="btn btn-outline-primary">Word Counter</a>
      </div>
    </div>

  </div>
</div>

      <Footer />

    </>
  );
}